import { z } from 'zod';
import { GameId, SourceMode } from './game.ts';
import { CountThresholds, GameSelection, OddsLine } from './market.ts';
import { CameraAliasId, GameRoundId, NormPoint, Sha256Hex, UtcInstant } from './primitives.ts';
import { LightningOffer } from './promotion.ts';

export const RoundState = z.enum([
  'SCHEDULED',
  'BETTING_OPEN',
  'BETTING_CLOSED',
  'CAPTURING',
  'VERIFYING',
  'REVIEW_REQUIRED',
  'RESULT_FINAL',
  'SETTLING',
  'SETTLED',
  'VOIDING',
  'VOIDED',
]);
export type RoundState = z.infer<typeof RoundState>;

/** `bicycle_pushed`: a bicycle walked, not ridden — never counted by Cycle Count. */
export const VehicleClass = z.enum(['car', 'bus', 'truck', 'motorcycle', 'bicycle', 'bicycle_pushed', 'boat']);
export type VehicleClass = z.infer<typeof VehicleClass>;

/**
 * Direction relative to the segment a→b in image coordinates (y down):
 * A_TO_B = movement from the left-hand side to the right-hand side of a→b; B_TO_A = the reverse.
 */
export const CrossingDirection = z.enum(['A_TO_B', 'B_TO_A']);
export type CrossingDirection = z.infer<typeof CrossingDirection>;

export const CountingLine = z.object({
  lineId: z.string().min(1).max(64),
  /** Segment endpoints (not an infinite line), normalised to the intrinsic frame. */
  a: NormPoint,
  b: NormPoint,
  countedDirection: CrossingDirection,
  /**
   * Curved line: the point the line passes through at its middle (quadratic Bezier from a to b with control
   * point 2·curve − (a+b)/2). Absent = straight segment.
   */
  curve: NormPoint.optional(),
  /**
   * Display only: near edge of the approach zone drawn in front of the line (a′ beside a, b′ beside b).
   * Counting never uses it. Absent = derived from the line (domain `lineZone`).
   */
  zone: z.object({ a: NormPoint, b: NormPoint }).optional(),
});
export type CountingLine = z.infer<typeof CountingLine>;

export const RoundTiming = z.object({
  bettingOpenAt: UtcInstant,
  bettingCloseAt: UtcInstant,
  /** Earliest capture time of physical events that count. Must satisfy closeAt + guard < captureStartAt - clockError. */
  captureStartAt: UtcInstant,
  /** Exclusive end of the capture window [captureStartAt, captureEndAt). */
  captureEndAt: UtcInstant,
  guardMs: z.number().int().nonnegative(),
  clockErrorBoundMs: z.number().int().nonnegative().nullable(),
  verifyDeadlineAt: UtcInstant,
});
export type RoundTiming = z.infer<typeof RoundTiming>;

/** Which crossings count for a rule: only the line's counted direction, or both. */
export const DirectionMode = z.enum(['COUNTED_DIRECTION', 'ANY']);

/** Game mechanics, locked in the manifest. Each kind has its own evaluator (packages/domain/src/games.ts). */
export const GameRules = z.discriminatedUnion('kind', [
  /** Traffic Pulse, Cycle Count: total count vs L/H bands. */
  z.object({ kind: z.literal('COUNT_BANDS'), thresholds: CountThresholds, directions: DirectionMode }),
  /** Harbor Count: disjoint count buckets. */
  z.object({
    kind: z.literal('COUNT_BUCKETS'),
    buckets: z.array(z.object({ selection: GameSelection, min: z.number().int().nonnegative(), max: z.number().int().nonnegative().nullable() })).min(2),
    directions: DirectionMode,
  }),
  /** Lane Duel: A = A_TO_B crossings, B = B_TO_A crossings; equal (incl. 0–0) is TIE. */
  z.object({ kind: z.literal('DUEL'), labelA: z.string().max(40), labelB: z.string().max(40) }),
  /** Next Class: class group of the first counted crossing; none → NO_EVENT. */
  z.object({
    kind: z.literal('FIRST_EVENT'),
    groups: z.array(z.object({ selection: GameSelection, classes: z.array(VehicleClass).min(1) })).min(1),
    /** Different-group crossings closer than this to the first one make the round void. */
    simultaneityMs: z.number().int().positive(),
    directions: DirectionMode,
  }),
]);
export type GameRules = z.infer<typeof GameRules>;

/** Locked before betting opens. Any change requires a new round. */
export const RoundManifest = z.object({
  manifestVersion: z.literal(1),
  gameRoundId: GameRoundId,
  gameId: GameId,
  rulesVersion: z.string().min(1),
  rulesHash: Sha256Hex,
  modelVersion: z.string().min(1),
  modelHash: Sha256Hex.nullable(),
  configHash: Sha256Hex,
  cameraAlias: CameraAliasId,
  sourceMode: SourceMode,
  /** Media timeline the round is bound to; null for SIMULATION rounds without media. */
  sourceEpoch: z.string().min(1).max(160).nullable(),
  /** Counting window in media time of sourceEpoch, [start, end). Null for SIMULATION rounds. */
  captureWindowPts: z.object({ startMicros: z.number().int().nonnegative(), endMicros: z.number().int().nonnegative() }).nullable(),
  roi: z.array(NormPoint).min(3),
  line: CountingLine,
  includedClasses: z.array(VehicleClass).min(1),
  timing: RoundTiming,
  /** Band thresholds for COUNT_BANDS games (UI convenience); null for other mechanics. */
  thresholds: CountThresholds.nullable(),
  /**
   * Where the bands of a live-camera round come from: the camera's own recent counting windows (same
   * line). Locked before betting like everything else here. Absent = the game's configured thresholds.
   */
  bands: z
    .object({ source: z.literal('CAMERA_RECENT'), windows: z.number().int().positive(), median: z.number().nonnegative(), fromAt: UtcInstant, toAt: UtcInstant, /** The window counts the bands were placed from (fair-odds analysis; absent on older rounds). */ counts: z.array(z.number().int().nonnegative()).max(60).optional() })
    .optional(),
  rules: GameRules,
  oddsVersion: z.string().min(1),
  odds: z.array(OddsLine).min(1),
  /**
   * Where the odds come from (P14). EXAMPLE: illustrative fixture prices. MODEL: a validated traffic
   * model of this source (model id, margin, the probabilities and their bootstrap interval), locked
   * like everything else. A model price is not a return guarantee. Absent on manifests before P14.
   */
  pricing: z
    .object({
      source: z.enum(['EXAMPLE', 'MODEL']),
      modelId: z.string().nullable(),
      edgeBps: z.number().int().nonnegative().nullable(),
      quotes: z.array(z.object({ selection: GameSelection, p: z.number(), pLow: z.number(), pHigh: z.number() })),
      /** Set when Lightning was not offered because, under the model, it would leave less than the minimum margin. */
      lightningWithheld: z.string().nullable().optional(),
    })
    .optional(),
  reviewPolicyVersion: z.string().min(1),
  voidPolicyVersion: z.string().min(1),
  promoCommitment: Sha256Hex.nullable(),
  /** Lightning boosts shown at betting open (null when the feature is off). Never affects the count. */
  lightning: LightningOffer.nullable(),
  /**
   * What the source's timestamps mean and how far they can be trusted (null for SIMULATION). A source
   * whose capture clock is not trusted with a measured bound is never monetary.
   */
  sourceClock: z
    .object({
      timestampKind: z.enum(['CAPTURE', 'PUBLISH', 'INGEST', 'SIMULATED_CAPTURE', 'UNKNOWN']),
      trust: z.enum(['TRUSTED', 'UNTRUSTED', 'UNKNOWN']),
      errorBoundMs: z.number().int().nonnegative(),
      errorBoundMeasured: z.boolean(),
      monetaryEligible: z.boolean(),
    })
    .nullable()
    .optional(),
  /**
   * Live-camera rounds: the camera and where it is, locked before betting (shown in the intro). Only
   * metadata we actually hold; missing parts are null, never invented.
   */
  camera: z
    .object({
      cameraId: z.string(),
      name: z.string().nullable(),
      provider: z.string(),
      countryCode: z.string().nullable(),
      country: z.string().nullable(),
      city: z.string().nullable(),
      road: z.string().nullable(),
      region: z.string().nullable().optional(),
      district: z.string().nullable().optional(),
      /** Required attribution for geocoded fields (e.g. OpenStreetMap), shown with them. */
      locationAttribution: z.string().nullable().optional(),
      latitude: z.number().nullable(),
      longitude: z.number().nullable(),
      rightsStatus: z.string(),
      calibrationVersion: z.number().int(),
      calibrationStatus: z.enum(['PROVISIONAL', 'VALIDATED']),
    })
    .nullable()
    .optional(),
});
export type RoundManifest = z.infer<typeof RoundManifest>;

export const RoundSnapshot = z.object({
  gameRoundId: GameRoundId,
  state: RoundState,
  manifest: RoundManifest,
  count: z.number().int().nonnegative(),
  finalCount: z.number().int().nonnegative().nullable(),
  /** Set once the result is final; null before, and for voided rounds. */
  winningSelections: z.array(z.string()).nullable(),
  /** Live/final figures per mechanic (count; A/B for duels; first class for Next Class). */
  metrics: z.object({
    count: z.number().int().nonnegative(),
    countA: z.number().int().nonnegative().nullable(),
    countB: z.number().int().nonnegative().nullable(),
    firstClass: z.string().nullable(),
  }),
  sequence: z.number().int().nonnegative(),
  /** Live-camera rounds: server-authoritative playback of the locked round media (null until ready). */
  playback: z.object({ assetId: z.string(), startAt: UtcInstant, revealAt: UtcInstant, durationMs: z.number().int().positive() }).nullable().optional(),
});
export type RoundSnapshot = z.infer<typeof RoundSnapshot>;
